import React, { useEffect, useState } from "react";
import axios from "axios";
import { Table, Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { toast } from "react-toastify";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import AdminPageShell, { AdminSurface } from "../../../components/Admin/AdminPageShell";
import { getApiUrl, getMediaUrl } from "../../../utils/site";
import "../../../styles/orderDetails.css";

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapModal, setMapModal] = useState(false);
  const [mapOrder, setMapOrder] = useState(null);
  const [detailModal, setDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/orders"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error("Failed to fetch orders.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getLatLng = (order) => {
    if (!order?.delivery_map_url) return null;
    const match = order.delivery_map_url.match(/q=([\d.]+),([\d.]+)/);
    if (!match) return null;
    return [parseFloat(match[1]), parseFloat(match[2])];
  };

  const handleRowClick = (order) => {
    setSelectedOrder(order);
    setDetailModal(true);
  };

  return (
    <AdminPageShell
      eyebrow="Commerce operations"
      title="Orders"
      description="Monitor all customer orders, addresses, payment state, and delivery progress from one professional operations desk."
    >
      <AdminSurface title="Order pipeline" subtitle={`${orders.length} orders currently recorded`}>
        {loading ? (
          <div className="roha-admin-empty">
            <Spinner color="info" /> Loading orders...
          </div>
        ) : (
          <div className="roha-admin-table-wrap">
            <Table responsive className="roha-admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Image</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th>Map</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Order</th>
                  <th>Payment</th>
                  <th>Delivery</th>
                </tr>
              </thead>
              <tbody>
                {orders.length ? (
                  [...orders]
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map((order, index) => (
                      <tr 
                        key={order.id} 
                        onClick={() => handleRowClick(order)}
                        style={{ cursor: "pointer" }}
                        className="roha-admin-table-row-hover"
                      >
                        <td data-label="#">{index + 1}</td>
                        <td data-label="Image">
                          {order.items?.[0]?.image_url ? (
                            <img
                              src={getMediaUrl(order.items[0].image_url)}
                              alt="Product"
                              className="roha-admin-image-thumb"
                              style={{ width: 48, height: 48 }}
                            />
                          ) : (
                            "-"
                          )}
                        </td>
                        <td data-label="Customer">{order.customer_name || order.full_name || "-"}</td>
                        <td data-label="Phone">{order.customer_phone || "-"}</td>
                        <td data-label="Email">{order.customer_email || "-"}</td>
                        <td data-label="Address">
                          {order.delivery_address_line1}
                          <br />
                          {order.delivery_city}, {order.delivery_state}
                        </td>
                        <td data-label="Map">
                          {order.delivery_map_url && getLatLng(order) ? (
                            <Button 
                              color="primary" 
                              size="sm" 
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setMapOrder(order); 
                                setMapModal(true); 
                              }}
                            >
                              View map
                            </Button>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td data-label="Items">
                          {order.items?.length ? (
                            <ul className="mb-0 ps-3 roha-admin-table-list">
                              {order.items.map((item, idx) => (
                                <li key={idx}>
                                  {item.product_name || item.name} x {item.quantity}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td data-label="Total">ETB {order.total}</td>
                        <td data-label="Order"><span className="roha-admin-pill">{order.status}</span></td>
                        <td data-label="Payment"><span className="roha-admin-pill">{order.payment_status_from_payments || order.payment_status}</span></td>
                        <td data-label="Delivery"><span className="roha-admin-pill">{order.delivery_status}</span></td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="12" className="roha-admin-empty">
                      No orders found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </AdminSurface>

      <Modal isOpen={mapModal} toggle={() => setMapModal(false)} size="lg">
        <ModalHeader toggle={() => setMapModal(false)}>Delivery location map</ModalHeader>
        <ModalBody>
          {mapOrder && getLatLng(mapOrder) ? (
            <div style={{ height: 350, width: "100%" }}>
              <MapContainer center={getLatLng(mapOrder)} zoom={15} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker
                  position={getLatLng(mapOrder)}
                  icon={L.icon({
                    iconUrl: "https://cdn-icons-png.flaticon.com/512/854/854878.png",
                    iconSize: [32, 32],
                  })}
                >
                  <Popup>Delivery location</Popup>
                </Marker>
              </MapContainer>
            </div>
          ) : (
            <div>No map data available.</div>
          )}
        </ModalBody>
        <ModalFooter>
          {mapOrder?.delivery_map_url ? (
            <a href={mapOrder.delivery_map_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              Get directions
            </a>
          ) : null}
          <Button color="secondary" onClick={() => setMapModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Order Details Modal */}
      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="xl" className="order-detail-modal">
        <ModalHeader toggle={() => setDetailModal(false)} className="order-detail-header">
          <div>
            <h4 style={{ margin: 0, color: "var(--color-heading)" }}>Order Details</h4>
            <p style={{ margin: 0, color: "var(--color-muted)", fontSize: "0.9rem" }}>
              Order #{selectedOrder?.id} • {selectedOrder?.created_at ? new Date(selectedOrder.created_at).toLocaleDateString() : ''}
            </p>
          </div>
        </ModalHeader>
        <ModalBody className="order-detail-body">
          {selectedOrder && (
            <div className="order-detail-content">
              {/* Status Overview */}
              <div className="order-status-overview">
                <div className="status-card">
                  <h6>Order Status</h6>
                  <span className={`status-badge status-${selectedOrder.status?.toLowerCase()}`}>
                    {selectedOrder.status || 'Pending'}
                  </span>
                </div>
                <div className="status-card">
                  <h6>Payment Status</h6>
                  <span className={`status-badge status-${(selectedOrder.payment_status_from_payments || selectedOrder.payment_status)?.toLowerCase()}`}>
                    {selectedOrder.payment_status_from_payments || selectedOrder.payment_status || 'Pending'}
                  </span>
                </div>
                <div className="status-card">
                  <h6>Delivery Status</h6>
                  <span className={`status-badge status-${selectedOrder.delivery_status?.toLowerCase()}`}>
                    {selectedOrder.delivery_status || 'Pending'}
                  </span>
                </div>
                <div className="status-card">
                  <h6>Total Amount</h6>
                  <span className="total-amount">ETB {selectedOrder.total}</span>
                </div>
              </div>

              <div className="order-detail-grid">
                {/* Customer Information */}
                <div className="detail-section">
                  <h5>Customer Information</h5>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Name:</label>
                      <span>{selectedOrder.customer_name || selectedOrder.full_name || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <label>Email:</label>
                      <span>{selectedOrder.customer_email || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <label>Phone:</label>
                      <span>{selectedOrder.customer_phone || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <label>Order Date:</label>
                      <span>{selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString() : 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Delivery Information */}
                <div className="detail-section">
                  <h5>Delivery Information</h5>
                  <div className="info-grid">
                    <div className="info-item full-width">
                      <label>Address:</label>
                      <span>
                        {selectedOrder.delivery_address_line1}<br/>
                        {selectedOrder.delivery_address_line2 && <>{selectedOrder.delivery_address_line2}<br/></>}
                        {selectedOrder.delivery_city}, {selectedOrder.delivery_state}<br/>
                        {selectedOrder.delivery_postal_code}
                      </span>
                    </div>
                    {selectedOrder.delivery_map_url && (
                      <div className="info-item">
                        <label>Map:</label>
                        <Button 
                          color="primary" 
                          size="sm" 
                          onClick={() => { 
                            setMapOrder(selectedOrder); 
                            setMapModal(true); 
                          }}
                        >
                          View Location
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="detail-section">
                <h5>Order Items ({selectedOrder.items?.length || 0} items)</h5>
                <div className="order-items-list">
                  {selectedOrder.items?.length ? (
                    selectedOrder.items.map((item, index) => (
                      <div key={index} className="order-item-card">
                        <div className="item-image">
                          {item.image_url ? (
                            <img
                              src={getMediaUrl(item.image_url)}
                              alt={item.product_name || item.name}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/placeholder-image.jpg';
                              }}
                            />
                          ) : (
                            <div className="no-image">No Image</div>
                          )}
                        </div>
                        <div className="item-details">
                          <h6>{item.product_name || item.name}</h6>
                          <p className="item-description">{item.description || 'No description available'}</p>
                          <div className="item-specs">
                            {item.material && <span className="spec">Material: {item.material}</span>}
                            {item.weight && <span className="spec">Weight: {item.weight}g</span>}
                            {item.size && <span className="spec">Size: {item.size}</span>}
                          </div>
                        </div>
                        <div className="item-pricing">
                          <div className="quantity">Qty: {item.quantity}</div>
                          <div className="unit-price">ETB {item.price} each</div>
                          <div className="total-price">ETB {(item.price * item.quantity).toFixed(2)}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-items">No items found for this order.</div>
                  )}
                </div>
              </div>

              {/* Payment Information */}
              {(selectedOrder.payment_method || selectedOrder.payment_reference) && (
                <div className="detail-section">
                  <h5>Payment Information</h5>
                  <div className="info-grid">
                    {selectedOrder.payment_method && (
                      <div className="info-item">
                        <label>Payment Method:</label>
                        <span>{selectedOrder.payment_method}</span>
                      </div>
                    )}
                    {selectedOrder.payment_reference && (
                      <div className="info-item">
                        <label>Payment Reference:</label>
                        <span>{selectedOrder.payment_reference}</span>
                      </div>
                    )}
                    {selectedOrder.payment_date && (
                      <div className="info-item">
                        <label>Payment Date:</label>
                        <span>{new Date(selectedOrder.payment_date).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Order Summary */}
              <div className="detail-section">
                <h5>Order Summary</h5>
                <div className="order-summary">
                  <div className="summary-row">
                    <span>Subtotal:</span>
                    <span>ETB {selectedOrder.subtotal || selectedOrder.total}</span>
                  </div>
                  {selectedOrder.tax && (
                    <div className="summary-row">
                      <span>Tax:</span>
                      <span>ETB {selectedOrder.tax}</span>
                    </div>
                  )}
                  {selectedOrder.shipping && (
                    <div className="summary-row">
                      <span>Shipping:</span>
                      <span>ETB {selectedOrder.shipping}</span>
                    </div>
                  )}
                  <div className="summary-row total">
                    <span>Total:</span>
                    <span>ETB {selectedOrder.total}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="order-detail-footer">
          <Button color="secondary" onClick={() => setDetailModal(false)}>
            Close
          </Button>
          <Button color="primary" onClick={() => window.print()}>
            Print Order
          </Button>
        </ModalFooter>
      </Modal>
    </AdminPageShell>
  );
};

export default OrderManagement;
